/** Player MQ event subscriptions → smart-restart model. */

import type { TPlayer } from '@setplex/pureya'
import type { PureyaPlayerError } from '~/shared/smart-restart'

import { Events } from '@setplex/pureya'
import { createEffect, sample, scopeBind } from 'effector'
import { mq, PLAYER } from '~/shared/mq'
import { toSmartRetryError } from '~/shared/smart-restart'
import {
  $enabled,
  contentSwitched,
  errorDetected,
  init,
  loadStarted,
  manualRetryStarted,
  playbackRestored,
} from './model'

type MqVjsEvent = { player: TPlayer; payload?: unknown }

type PlayerWithSmartRestartOption = TPlayer & {
  options_?: { smartRestart?: boolean }
}

/**
 * Preview/background trailers must not drive Smart-Restart (FP-4169).
 * Pureya `trailerPlayer` sets `options.smartRestart = false`. Prefer that
 * player option over `$isTrailerFlowActive`: the flow flag can be cleared by
 * `stopTrailerFlow` before an async playback error arrives on MQ.
 */
export function isSmartRestartDisabled(player: TPlayer | undefined): boolean {
  return (
    (player as PlayerWithSmartRestartOption | undefined)?.options_
      ?.smartRestart === false
  )
}

/**
 * True when the current source is played in native mode (no MSE/VHS pipeline):
 * the tech exists but exposes no `vhs`, meaning the browser plays the media
 * directly (native HLS on Safari/iOS, progressive mp4, …). Used to skip
 * automatic retries per CR-404 AC-9.
 *
 * Must be sampled on `loadstart` (or later, while playback is healthy), NOT at
 * error time: the error overlay calls `player.resetSoft()` which reloads the
 * Html5 tech and drops its `vhs` handler, so at error time every stream would
 * look native. VHS attaches `tech.vhs` synchronously when the source is set,
 * i.e. before `loadstart` fires, so `loadstart` reflects the real pipeline.
 * The value is persisted in the model's `$isNativeSource` store and folded into
 * the error there.
 */
function isNativePlayback(player: TPlayer | undefined): boolean {
  const tech = (
    player as unknown as
      | { tech?: (safety: boolean) => { vhs?: unknown } | undefined }
      | undefined
  )?.tech?.(true)

  return tech != null && tech.vhs == null
}

const attachListenersFx = createEffect(() => {
  const _errorDetected = scopeBind(errorDetected, { safe: true })
  const _playbackRestored = scopeBind(playbackRestored, { safe: true })
  const _manualRetryStarted = scopeBind(manualRetryStarted, { safe: true })
  const _contentSwitched = scopeBind(contentSwitched, { safe: true })
  const _loadStarted = scopeBind(loadStarted, { safe: true })

  /** Source load started → record native-mode before an error can tear down VHS. */
  mq.on<MqVjsEvent>(`${PLAYER}/loadstart`, (msg) => {
    if (isSmartRestartDisabled(msg?.player)) return
    _loadStarted({ isNative: isNativePlayback(msg?.player) })
  })

  /** Player error → errorDetected (non-recoverable codes filtered by controller). */
  mq.on<MqVjsEvent>(`${PLAYER}/error`, (msg) => {
    if (!$enabled.getState()) return
    if (isSmartRestartDisabled(msg?.player)) return

    const error = msg?.player?.error() as PureyaPlayerError | null
    if (!error) return

    const retryError = toSmartRetryError(error)
    if (!retryError) return

    _errorDetected(retryError)
  })

  /** Playback resumed → clear retry counter. */
  mq.on<MqVjsEvent>(`${PLAYER}/playing`, (msg) => {
    if (!$enabled.getState()) return
    if (isSmartRestartDisabled(msg?.player)) return
    _playbackRestored()
  })

  /** Retry button → manualRetryStarted; reloadUrl is triggered in model.ts after the controller step change. */
  mq.on(`${PLAYER}/${Events.Player.Retry}`, () => {
    if (!$enabled.getState()) return
    _manualRetryStarted()
  })

  /** Content switched → contentSwitched (per-content counts preserved). */
  mq.on<MqVjsEvent>(`${PLAYER}/${Events.Media.ContentChanged}`, (msg) => {
    if (!$enabled.getState()) return
    if (isSmartRestartDisabled(msg?.player)) return
    _contentSwitched(msg?.payload as string | number | undefined)
  })
})

sample({ clock: init, target: attachListenersFx })
